// BoostRechargeStripe.jsx
// ----------------------------------------------------------------------------
// Phase 5 — real Stripe-driven recharge for NuVe Boost credit.
//
// Two-step flow:
//   1. createIntent() → POST /boost/creditRechargeIntent → returns client_secret
//   2. <Elements> → <PaymentElement> → stripe.confirmPayment
//
// Once confirmPayment succeeds we IMMEDIATELY call onPaymentApplied —
// no polling, no spinner. The parent moves the modal to its success
// step and runs its own background `fetchBoostCredit` to pick up the
// webhook landing. Two reasons we don't poll here:
//
//   * Polling inside the modal re-renders the component on every
//     Redux tick, which means a fresh `options` object literal hits
//     <Elements> on every render, which makes Stripe re-mount
//     PaymentElement → user sees the card form "come back" right
//     after the payment-received spinner. The fix is to unmount this
//     whole subtree the moment payment is confirmed.
//
//   * The webhook IS the source of truth. The frontend doesn't need
//     to wait for it before celebrating — the customer's money is
//     already with Stripe.
//
// Payment methods are restricted to Card + Apple Pay + Google Pay by
// the server-side automatic_payment_methods.allow_redirects=NEVER on
// the PaymentIntent.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import AuthUtil from '../../redux/AuthUtil';
import { BOOST, STRIPE as STRIPE_ENDPOINTS } from '../global/Server/endpoints';

const C = {
  white: '#fff',
  text: '#1a1d2e',
  textMuted: '#8b90a8',
  border: '#e2e5f1',
  inputBg: '#f0f1f7',
  accent: '#5b4cdb',
  green: '#0fa968',
  red: '#e5443a',
  greenPale: '#e6f9ef',
  redPale: '#fff0f0',
  greenGlow: 'rgba(15,169,104,0.12)',
};
const gradG = `linear-gradient(135deg,${C.green},#059669)`;

// Memoize the Stripe.js promise across mounts. loadStripe is meant to
// be called exactly once per session — calling it again downloads
// stripe.js a second time and splits Stripe's internal state.
let cachedStripePromise = null;
async function ensureStripePromise() {
  if (cachedStripePromise) return cachedStripePromise;
  const res = await AuthUtil.getRequest(STRIPE_ENDPOINTS.PUBLIC_CONFIG);
  const key = res?.data?.publishableKey;
  if (!key) throw new Error(res?.data?.message || 'Stripe is not configured');
  cachedStripePromise = loadStripe(key);
  return cachedStripePromise;
}

/**
 * Inner form — must live inside <Elements> to use useStripe/useElements.
 * Note this no longer owns any "post-payment" UI state — once
 * confirmPayment resolves with no error we hand control back to the
 * parent immediately. That's what stops the visa-dialog flash-back.
 */
// Small plan/fee/total summary so the customer sees exactly what their card
// is charged before confirming. The grossed-up total is also what Apple Pay /
// Google Pay show, because the PaymentIntent amount IS the total.
function RechargeSummary({ amount, quote }) {
  const money = (v) => `$${Number(v || 0).toFixed(2)}`;
  const baseUSD = quote?.baseUSD != null ? quote.baseUSD : amount;
  const totalUSD = quote?.totalUSD != null ? quote.totalUSD : amount;
  const hasFee = quote?.feeUSD != null && Number(quote.feeUSD) > 0;
  const row = (label, value, strong) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: strong ? 0 : 6,
      }}
    >
      <span
        style={{
          fontSize: strong ? 14 : 13,
          fontWeight: strong ? 700 : 500,
          color: strong ? C.text : C.textMuted,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: strong ? 17 : 13,
          fontWeight: strong ? 800 : 600,
          color: C.text,
        }}
      >
        {value}
      </span>
    </div>
  );
  return (
    <div
      style={{
        background: C.inputBg,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '12px 14px',
        marginBottom: 16,
      }}
    >
      {hasFee && (
        <>
          {row('Boost credit', money(baseUSD))}
          {row('Processing fee', money(quote.feeUSD))}
          <div
            style={{
              borderTop: `1px solid ${C.border}`,
              margin: '8px 0',
            }}
          />
        </>
      )}
      {row('Total', money(totalUSD), true)}
    </div>
  );
}

function PaymentForm({ amount, quote, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const totalUSD = quote?.totalUSD != null ? quote.totalUSD : amount;
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  // Hard guard against double-submit. The Pay button is also disabled
  // while submitting, but a fast double-click can still race state.
  const inFlight = useRef(false);

  const onSubmit = async () => {
    if (!stripe || !elements) return;
    if (inFlight.current) return;
    inFlight.current = true;
    setErr(null);
    setSubmitting(true);
    // redirect: 'if_required' keeps the customer in-page when the
    // payment method doesn't need a 3DS redirect (cards usually
    // don't; wallets never do). return_url is mandatory per Stripe.
    let result;
    try {
      result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });
    } catch (e) {
      // Network failure during confirmation — rare; surface and let
      // the user retry.
      inFlight.current = false;
      setErr(e.message || 'Payment could not be submitted');
      setSubmitting(false);
      return;
    }
    if (result?.error) {
      // Card decline, validation failure, 3DS denied, etc. Stripe
      // already showed the decline reason inside PaymentElement, but
      // we surface it again above the Pay button so it's not buried.
      inFlight.current = false;
      setErr(result.error.message || 'Payment failed');
      setSubmitting(false);
      return;
    }

    // Payment confirmed on Stripe's side. Now tell our backend to
    // verify with Stripe's API and apply the credit — same crediting
    // logic the webhook uses, just initiated by us instead of waiting
    // for Stripe → us. Idempotent on the server, so if the webhook
    // ALSO arrives later it's a safe no-op.
    const pi = result?.paymentIntent;
    if (pi?.id) {
      try {
        const sync = await AuthUtil.postRequest(BOOST.CREDIT_RECHARGE_SYNC, {
          paymentIntentId: pi.id,
        });
        if (!sync?.data?.success) {
          // The payment went through but our credit didn't land.
          // Show a clear message — the customer's money is safe in
          // Stripe and the webhook will retry, but they deserve to
          // know what happened.
          inFlight.current = false;
          setErr(
            (sync?.data?.message ||
              'Payment succeeded but credit could not be applied. Refresh in a moment.') +
              ' (PI: ' +
              pi.id +
              ')'
          );
          setSubmitting(false);
          return;
        }
      } catch (e) {
        // Network failure on the sync call. The webhook will still
        // back-stop it eventually, so we let the user proceed — but
        // tell them their balance may take a moment to update.
        // (We deliberately don't bail here.)
      }
    }

    // Success and credit applied. Hand off to parent — DO NOT
    // setSubmitting(false): the whole component is about to unmount.
    onSuccess();
  };

  return (
    <>
      <RechargeSummary amount={amount} quote={quote} />
      <PaymentElement options={{ layout: 'tabs' }} />
      {err && (
        <div
          style={{
            marginTop: 12,
            padding: '8px 12px',
            background: C.redPale,
            borderRadius: 8,
            fontSize: 12,
            color: C.red,
          }}
        >
          {err}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          style={{
            flex: 1,
            padding: 14,
            background: 'none',
            border: `1.5px solid ${C.border}`,
            borderRadius: 12,
            color: C.textMuted,
            fontSize: 14,
            fontWeight: 500,
            cursor: submitting ? 'default' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting || !stripe || !elements}
          style={{
            flex: 2,
            padding: 14,
            background: submitting ? C.border : gradG,
            border: 'none',
            borderRadius: 12,
            color: '#fff',
            fontSize: 15,
            fontWeight: 700,
            cursor: submitting ? 'default' : 'pointer',
            boxShadow: submitting ? 'none' : `0 6px 20px ${C.greenGlow}`,
          }}
        >
          {submitting ? 'Processing…' : `Pay $${Number(totalUSD).toFixed(2)}`}
        </button>
      </div>
    </>
  );
}

/**
 * Outer wrapper — fetches publishable key + clientSecret, then mounts
 * <Elements>. clientSecret is locked in on first mount and never
 * re-fetched; that way the parent re-rendering (Redux updates while
 * the modal is open, etc.) never forces Stripe to re-init.
 */
export default function BoostRechargeStripe({
  locationId,
  locationName,
  amount,
  onPaymentApplied,
  onCancel,
}) {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  // Plan/fee/total breakdown returned alongside the clientSecret. Drives the
  // summary + Pay-button total. Null until the intent is created (or when the
  // server has fee pass-through disabled and returns only the base).
  const [quote, setQuote] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const fetched = useRef(false);

  useEffect(() => {
    // Idempotent fetch — even if React invokes the effect twice in
    // StrictMode dev builds, we only create one PaymentIntent.
    if (fetched.current) return;
    fetched.current = true;
    let cancelled = false;
    (async () => {
      try {
        const promise = await ensureStripePromise();
        if (!cancelled) setStripePromise(promise);
      } catch (e) {
        if (!cancelled) setLoadErr(e.message);
        return;
      }
      try {
        const res = await AuthUtil.postRequest(BOOST.CREDIT_RECHARGE_INTENT, {
          locationId,
          locationName,
          amount,
        });
        if (cancelled) return;
        if (res?.data?.success && res.data.clientSecret) {
          setClientSecret(res.data.clientSecret);
          setQuote({
            baseUSD: res.data.baseUSD,
            feeUSD: res.data.feeUSD,
            totalUSD: res.data.totalUSD,
          });
        } else {
          setLoadErr(res?.data?.message || 'Could not create payment');
        }
      } catch (e) {
        if (!cancelled) setLoadErr('Could not reach payment service');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only; locationId+amount are captured by the closure

  // Memoize the Elements `options`. Stripe's Elements provider keys off
  // object identity for some internal checks; passing a new object on
  // every render is what made PaymentElement re-mount after the parent
  // re-rendered during the (now-removed) polling loop. Locking the
  // object reference keeps Stripe quiet.
  const elementsOptions = useMemo(
    () =>
      clientSecret
        ? {
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: { colorPrimary: C.accent, borderRadius: '10px' },
            },
          }
        : null,
    [clientSecret]
  );

  if (loadErr) {
    return (
      <div style={{ padding: '20px 0', textAlign: 'center' }}>
        <div
          style={{
            padding: '10px 14px',
            background: C.redPale,
            borderRadius: 10,
            fontSize: 13,
            color: C.red,
            marginBottom: 16,
          }}
        >
          {loadErr}
        </div>
        <button
          onClick={onCancel}
          style={{
            padding: '10px 24px',
            background: 'none',
            border: `1.5px solid ${C.border}`,
            borderRadius: 10,
            color: C.textMuted,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    );
  }

  if (!stripePromise || !elementsOptions) {
    return (
      <div
        style={{
          padding: '36px 0',
          textAlign: 'center',
          color: C.textMuted,
          fontSize: 13,
        }}
      >
        Loading secure checkout…
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <PaymentForm
        amount={amount}
        quote={quote}
        onSuccess={onPaymentApplied}
        onCancel={onCancel}
      />
    </Elements>
  );
}
